import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Switch,
  Slider,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import dayjs from 'dayjs';

const WorkOrderForm = ({ workOrder, onSave, onCancel }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [uploadFiles, setUploadFiles] = useState({});

  const steps = [
    'Basic Information',
    'Contractor Assignment', 
    'Work Location',
    'Timeline',
    'Worker Requirements',
    'Financial Details',
    'Work Scope',
    'Safety & Compliance',
    'Documents',
    'Status Tracking'
  ];

  const [formData, setFormData] = useState({
    // Tab 1: Basic Information
    basicInformation: {
      title: '',
      workOrderNumber: '',
      workOrderType: 'construction',
      priority: 'medium',
      urgency: 'normal',
      description: '',
      department: '',
      projectCode: '',
      clientReference: ''
    },

    // Tab 2: Contractor Assignment
    contractorAssignment: {
      primaryContractor: {
        contractorId: '',
        companyName: '',
        contactPerson: '',
        contactNumber: '',
        email: ''
      },
      subContractors: [],
      laborContractors: [],
      specialistContractors: []
    },

    // Tab 3: Work Location
    workLocation: {
      siteName: '',
      siteAddress: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        coordinates: {
          latitude: '',
          longitude: ''
        }
      },
      workAreas: [],
      facilities: {
        powerSupply: false,
        waterSupply: false,
        accommodation: false,
        canteen: false,
        medicalFacility: false,
        transportation: false
      },
      accessInstructions: ''
    },

    // Tab 4: Timeline
    timeline: {
      startDate: null,
      endDate: null,
      estimatedDuration: '',
      workingHours: {
        dailyHours: 8,
        workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
        shiftType: 'single'
      },
      milestones: [],
      criticalPath: [],
      bufferTime: 0,
      extensions: []
    },

    // Tab 5: Worker Requirements
    workerRequirements: {
      totalWorkers: 0,
      skillRequirements: [],
      designations: [],
      experienceLevel: {
        fresher: 0,
        experienced: 0,
        skilled: 0,
        supervisory: 0
      },
      shiftRequirements: {
        morning: 0,
        evening: 0,
        night: 0,
        rotational: 0
      },
      specialCertifications: [],
      languageRequirements: []
    },

    // Tab 6: Financial Details
    financialDetails: {
      contractValue: {
        totalAmount: 0,
        currency: 'INR',
        taxIncluded: false,
        gstRate: 18
      },
      paymentStructure: {
        advancePayment: 0,
        milestonePayments: [],
        finalPayment: 0,
        retentionAmount: 0,
        paymentTerms: 'net30'
      },
      costBreakdown: {
        labor: 0,
        materials: 0,
        equipment: 0,
        overhead: 0,
        profit: 0
      },
      budgetAllocations: [],
      invoiceSchedule: []
    },

    // Tab 7: Work Scope
    workScope: {
      mainTasks: [],
      deliverables: [],
      qualityParameters: {
        qualityStandards: [],
        testingRequirements: [],
        acceptanceCriteria: [],
        qualityCheckpoints: []
      },
      technicalSpecifications: '',
      materialsRequired: [],
      equipmentRequired: [],
      workMethodology: '',
      riskAssessment: []
    },

    // Tab 8: Safety & Compliance
    safetyCompliance: {
      safetyRequirements: {
        ppe: [],
        safetyTraining: [],
        safetyOfficer: false,
        firstAid: false,
        fireExtinguisher: false,
        emergencyProcedures: ''
      },
      permits: [],
      inspections: {
        dailySafetyChecks: false,
        weeklySafetyAudits: false,
        monthlyCompliance: false,
        thirdPartyInspection: false
      },
      complianceChecklist: [],
      environmentalRequirements: [],
      statutoryCompliance: []
    },

    // Tab 9: Documents
    documents: {
      workOrderDocument: null,
      technicalDrawings: [],
      contracts: [],
      permits: [],
      safetyDocuments: [],
      qualityDocuments: [],
      reports: [],
      correspondence: []
    },

    // Tab 10: Status Tracking
    statusTracking: {
      currentStatus: 'draft',
      overallProgress: 0,
      phaseProgress: [],
      qualityChecks: [],
      safetyIncidents: [],
      delaysReported: [],
      changeRequests: [],
      approvals: {
        technicalApproval: false,
        safetyApproval: false,
        financialApproval: false,
        clientApproval: false
      },
      nextActions: []
    }
  });

  const fetchContractors = useCallback(async () => {
    try {
      const response = await axios.get('/api/contractors', {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        }
      });
      // Handle the API response structure
      if (response.data.success) {
        setContractors(response.data.data.contractors || []);
      } else {
        setContractors([]);
      }
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  }, []);

  const fetchWorkOrderDetails = useCallback(async () => {
    if (workOrder?._id) {
      try {
        const response = await axios.get(`/api/work-order-details/${workOrder._id}`, {
          headers: { 
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            "x-tenant-id": localStorage.getItem("tenantId"),
          }
        });
        const workOrderData = response.data.workOrder || response.data;
        // Merge with existing formData structure to ensure all properties exist
        setFormData(prevFormData => ({
          ...prevFormData,
          ...workOrderData,
          basicInfo: {
            ...prevFormData.basicInfo,
            ...workOrderData.basicInfo
          },
          contractorAssignment: {
            ...prevFormData.contractorAssignment,
            ...workOrderData.contractorAssignment
          },
          workLocation: {
            ...prevFormData.workLocation,
            ...workOrderData.workLocation
          },
          timeline: {
            ...prevFormData.timeline,
            ...workOrderData.timeline
          },
          workerRequirements: {
            ...prevFormData.workerRequirements,
            ...workOrderData.workerRequirements
          },
          financialDetails: {
            ...prevFormData.financialDetails,
            ...workOrderData.financialDetails
          },
          workScope: {
            ...prevFormData.workScope,
            ...workOrderData.workScope
          },
          safetyCompliance: {
            ...prevFormData.safetyCompliance,
            ...workOrderData.safetyCompliance
          },
          statusTracking: {
            ...prevFormData.statusTracking,
            ...workOrderData.statusTracking
          }
        }));
      } catch (error) {
        console.error('Error fetching work order details:', error);
      }
    }
  }, [workOrder]);

  useEffect(() => {
    fetchContractors();
    if (workOrder) {
      fetchWorkOrderDetails();
    }
  }, [workOrder, fetchContractors, fetchWorkOrderDetails]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const formDataToSend = new FormData();
      
      // Append form data
      formDataToSend.append('workOrderData', JSON.stringify(formData));
      
      // Append files
      Object.keys(uploadFiles).forEach(key => {
        if (uploadFiles[key]) {
          formDataToSend.append(key, uploadFiles[key]);
        }
      });

      const url = workOrder?._id 
        ? `/api/work-order-details/${workOrder._id}`
        : '/api/work-order-details';
      
      const method = workOrder?._id ? 'put' : 'post';

      await axios[method](url, formDataToSend, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem("tenantId"),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (process.env.NODE_ENV !== 'production') {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload Progress: ${percentCompleted}%`);
          }
        }
      });

      onSave();
    } catch (error) {
      console.error('Error saving work order:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tab 1: Basic Information
  const renderBasicInfoStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Basic Work Order Information</Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Work Order Title *"
          value={formData.basicInformation?.title || ''}
          onChange={(e) => setFormData({
            ...formData,
            basicInformation: { ...formData.basicInformation, title: e.target.value }
          })}
          required
        />
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Work Order Number"
          value={formData.basicInformation?.workOrderNumber || ''}
          onChange={(e) => setFormData({
            ...formData,
            basicInformation: { ...formData.basicInformation, workOrderNumber: e.target.value }
          })}
          placeholder="Auto-generated if empty"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Work Order Type *</InputLabel>
          <Select
            value={formData.basicInformation?.workOrderType || ''}
            onChange={(e) => setFormData({
              ...formData,
              basicInformation: { ...formData.basicInformation, workOrderType: e.target.value }
            })}
            label="Work Order Type *"
          >
            <MenuItem value="construction">Construction</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
            <MenuItem value="repair">Repair</MenuItem>
            <MenuItem value="installation">Installation</MenuItem>
            <MenuItem value="inspection">Inspection</MenuItem>
            <MenuItem value="cleaning">Cleaning</MenuItem>
            <MenuItem value="security">Security</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Priority *</InputLabel>
          <Select
            value={formData.basicInfo?.priority || ''}
            onChange={(e) => setFormData({
              ...formData,
              basicInfo: { ...formData.basicInfo, priority: e.target.value }
            })}
            label="Priority *"
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Urgency</InputLabel>
          <Select
            value={formData.basicInfo?.urgency || ''}
            onChange={(e) => setFormData({
              ...formData,
              basicInfo: { ...formData.basicInfo, urgency: e.target.value }
            })}
            label="Urgency"
          >
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
            <MenuItem value="emergency">Emergency</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Description"
          value={formData.basicInfo?.description || ''}
          onChange={(e) => setFormData({
            ...formData,
            basicInfo: { ...formData.basicInfo, description: e.target.value }
          })}
          placeholder="Detailed description of the work order..."
        />
      </Grid>
    </Grid>
  );

  // Tab 2: Contractor Assignment
  const renderContractorAssignmentStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Contractor Assignment</Typography>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Primary Contractor</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Contractor *</InputLabel>
                  <Select
                    value={formData.contractorAssignment.primaryContractor.contractorId}
                    onChange={(e) => {
                      const selectedContractor = contractors.find(c => c._id === e.target.value);
                      setFormData({
                        ...formData,
                        contractorAssignment: {
                          ...formData.contractorAssignment,
                          primaryContractor: {
                            contractorId: e.target.value,
                            companyName: selectedContractor?.companyName || '',
                            contactPerson: selectedContractor?.contactDetails?.primaryContact?.name || '',
                            contactNumber: selectedContractor?.contactDetails?.primaryContact?.phone || '',
                            email: selectedContractor?.contactDetails?.primaryContact?.email || ''
                          }
                        }
                      });
                    }}
                    label="Select Contractor *"
                  >
                    {contractors.map((contractor) => (
                      <MenuItem key={contractor._id} value={contractor._id}>
                        {contractor.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contractorAssignment.primaryContractor.contactPerson}
                  onChange={(e) => setFormData({
                    ...formData,
                    contractorAssignment: {
                      ...formData.contractorAssignment,
                      primaryContractor: {
                        ...formData.contractorAssignment.primaryContractor,
                        contactPerson: e.target.value
                      }
                    }
                  })}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Sub-Contractors</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setFormData({
                  ...formData,
                  contractorAssignment: {
                    ...formData.contractorAssignment,
                    subContractors: [
                      ...formData.contractorAssignment.subContractors,
                      { contractorId: '', specialization: '', workScope: '' }
                    ]
                  }
                });
              }}
            >
              Add Sub-Contractor
            </Button>
            
            {formData.contractorAssignment.subContractors.map((subContractor, index) => (
              <Grid container spacing={2} key={index} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Contractor</InputLabel>
                    <Select
                      value={subContractor.contractorId}
                      onChange={(e) => {
                        const updatedSubContractors = [...formData.contractorAssignment.subContractors];
                        updatedSubContractors[index].contractorId = e.target.value;
                        setFormData({
                          ...formData,
                          contractorAssignment: {
                            ...formData.contractorAssignment,
                            subContractors: updatedSubContractors
                          }
                        });
                      }}
                      label="Contractor"
                    >
                      {contractors.map((contractor) => (
                        <MenuItem key={contractor._id} value={contractor._id}>
                          {contractor.companyName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Specialization"
                    value={subContractor.specialization}
                    onChange={(e) => {
                      const updatedSubContractors = [...formData.contractorAssignment.subContractors];
                      updatedSubContractors[index].specialization = e.target.value;
                      setFormData({
                        ...formData,
                        contractorAssignment: {
                          ...formData.contractorAssignment,
                          subContractors: updatedSubContractors
                        }
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Work Scope"
                    value={subContractor.workScope}
                    onChange={(e) => {
                      const updatedSubContractors = [...formData.contractorAssignment.subContractors];
                      updatedSubContractors[index].workScope = e.target.value;
                      setFormData({
                        ...formData,
                        contractorAssignment: {
                          ...formData.contractorAssignment,
                          subContractors: updatedSubContractors
                        }
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    color="error"
                    onClick={() => {
                      const updatedSubContractors = formData.contractorAssignment.subContractors.filter((_, i) => i !== index);
                      setFormData({
                        ...formData,
                        contractorAssignment: {
                          ...formData.contractorAssignment,
                          subContractors: updatedSubContractors
                        }
                      });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Tab 3: Work Location
  const renderWorkLocationStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Work Location Details</Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Site Name *"
          value={formData.workLocation.siteName}
          onChange={(e) => setFormData({
            ...formData,
            workLocation: { ...formData.workLocation, siteName: e.target.value }
          })}
          required
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>Site Address</Typography>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Street Address"
          value={formData.workLocation.siteAddress.street}
          onChange={(e) => setFormData({
            ...formData,
            workLocation: {
              ...formData.workLocation,
              siteAddress: { ...formData.workLocation.siteAddress, street: e.target.value }
            }
          })}
        />
      </Grid>
      
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="City"
          value={formData.workLocation.siteAddress.city}
          onChange={(e) => setFormData({
            ...formData,
            workLocation: {
              ...formData.workLocation,
              siteAddress: { ...formData.workLocation.siteAddress, city: e.target.value }
            }
          })}
        />
      </Grid>
      
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="State"
          value={formData.workLocation.siteAddress.state}
          onChange={(e) => setFormData({
            ...formData,
            workLocation: {
              ...formData.workLocation,
              siteAddress: { ...formData.workLocation.siteAddress, state: e.target.value }
            }
          })}
        />
      </Grid>
      
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Pincode"
          value={formData.workLocation.siteAddress.pincode}
          onChange={(e) => setFormData({
            ...formData,
            workLocation: {
              ...formData.workLocation,
              siteAddress: { ...formData.workLocation.siteAddress, pincode: e.target.value }
            }
          })}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>Available Facilities</Typography>
        <Grid container spacing={2}>
          {Object.keys(formData.workLocation.facilities).map((facility) => (
            <Grid item xs={6} sm={4} md={2} key={facility}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.workLocation.facilities[facility]}
                    onChange={(e) => setFormData({
                      ...formData,
                      workLocation: {
                        ...formData.workLocation,
                        facilities: {
                          ...formData.workLocation.facilities,
                          [facility]: e.target.checked
                        }
                      }
                    })}
                  />
                }
                label={facility.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  // Tab 4: Timeline
  const renderTimelineStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Project Timeline</Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Start Date *"
          value={formData.timeline?.startDate ? dayjs(formData.timeline.startDate) : null}
          onChange={(date) => setFormData({
            ...formData,
            timeline: { ...formData.timeline, startDate: date?.toDate() }
          })}
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="End Date *"
          value={formData.timeline?.endDate ? dayjs(formData.timeline.endDate) : null}
          onChange={(date) => setFormData({
            ...formData,
            timeline: { ...formData.timeline, endDate: date?.toDate() }
          })}
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Estimated Duration (days)"
          type="number"
          value={formData.timeline?.estimatedDuration || ''}
          onChange={(e) => setFormData({
            ...formData,
            timeline: { ...formData.timeline, estimatedDuration: e.target.value }
          })}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Daily Working Hours"
          type="number"
          value={formData.timeline?.workingHours?.dailyHours || ''}
          onChange={(e) => setFormData({
            ...formData,
            timeline: {
              ...formData.timeline,
              workingHours: { ...formData.timeline?.workingHours, dailyHours: e.target.value }
            }
          })}
          inputProps={{ min: 1, max: 24 }}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>Working Days</Typography>
        <Grid container spacing={1}>
          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
            <Grid item key={day}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.timeline?.workingHours?.workingDays?.includes(day) || false}
                    onChange={(e) => {
                      const currentWorkingDays = formData.timeline?.workingHours?.workingDays || [];
                      const workingDays = e.target.checked
                        ? [...currentWorkingDays, day]
                        : currentWorkingDays.filter(d => d !== day);
                      setFormData({
                        ...formData,
                        timeline: {
                          ...formData.timeline,
                          workingHours: { 
                            ...formData.timeline?.workingHours, 
                            workingDays 
                          }
                        }
                      });
                    }}
                  />
                }
                label={day.charAt(0).toUpperCase() + day.slice(1)}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  // Tab 5: Worker Requirements
  const renderWorkerRequirementsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Worker Requirements</Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Total Workers Required"
          type="number"
          value={formData.workerRequirements.totalWorkers}
          onChange={(e) => setFormData({
            ...formData,
            workerRequirements: { ...formData.workerRequirements, totalWorkers: parseInt(e.target.value) || 0 }
          })}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>Experience Level Distribution</Typography>
        <Grid container spacing={2}>
          {Object.keys(formData.workerRequirements.experienceLevel).map((level) => (
            <Grid item xs={6} sm={3} key={level}>
              <TextField
                fullWidth
                label={level.charAt(0).toUpperCase() + level.slice(1)}
                type="number"
                value={formData.workerRequirements.experienceLevel[level]}
                onChange={(e) => setFormData({
                  ...formData,
                  workerRequirements: {
                    ...formData.workerRequirements,
                    experienceLevel: {
                      ...formData.workerRequirements.experienceLevel,
                      [level]: parseInt(e.target.value) || 0
                    }
                  }
                })}
                inputProps={{ min: 0 }}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>Shift Requirements</Typography>
        <Grid container spacing={2}>
          {Object.keys(formData.workerRequirements.shiftRequirements).map((shift) => (
            <Grid item xs={6} sm={3} key={shift}>
              <TextField
                fullWidth
                label={shift.charAt(0).toUpperCase() + shift.slice(1)}
                type="number"
                value={formData.workerRequirements.shiftRequirements[shift]}
                onChange={(e) => setFormData({
                  ...formData,
                  workerRequirements: {
                    ...formData.workerRequirements,
                    shiftRequirements: {
                      ...formData.workerRequirements.shiftRequirements,
                      [shift]: parseInt(e.target.value) || 0
                    }
                  }
                })}
                inputProps={{ min: 0 }}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  // Tab 6: Financial Details
  const renderFinancialDetailsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Financial Details</Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Total Contract Value"
          type="number"
          value={formData.financialDetails.contractValue.totalAmount}
          onChange={(e) => setFormData({
            ...formData,
            financialDetails: {
              ...formData.financialDetails,
              contractValue: { ...formData.financialDetails.contractValue, totalAmount: parseFloat(e.target.value) || 0 }
            }
          })}
          InputProps={{
            startAdornment: <Typography>₹</Typography>
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="GST Rate (%)"
          type="number"
          value={formData.financialDetails.contractValue.gstRate}
          onChange={(e) => setFormData({
            ...formData,
            financialDetails: {
              ...formData.financialDetails,
              contractValue: { ...formData.financialDetails.contractValue, gstRate: parseFloat(e.target.value) || 0 }
            }
          })}
          inputProps={{ min: 0, max: 100 }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.financialDetails.contractValue.taxIncluded}
              onChange={(e) => setFormData({
                ...formData,
                financialDetails: {
                  ...formData.financialDetails,
                  contractValue: { ...formData.financialDetails.contractValue, taxIncluded: e.target.checked }
                }
              })}
            />
          }
          label="Tax Included in Contract Value"
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>Cost Breakdown</Typography>
        <Grid container spacing={2}>
          {Object.keys(formData.financialDetails.costBreakdown).map((cost) => (
            <Grid item xs={6} sm={2.4} key={cost}>
              <TextField
                fullWidth
                label={cost.charAt(0).toUpperCase() + cost.slice(1)}
                type="number"
                value={formData.financialDetails.costBreakdown[cost]}
                onChange={(e) => setFormData({
                  ...formData,
                  financialDetails: {
                    ...formData.financialDetails,
                    costBreakdown: {
                      ...formData.financialDetails.costBreakdown,
                      [cost]: parseFloat(e.target.value) || 0
                    }
                  }
                })}
                InputProps={{
                  startAdornment: <Typography>₹</Typography>
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  // Tab 7: Work Scope
  const renderWorkScopeStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Work Scope & Deliverables</Typography>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Technical Specifications"
          value={formData.workScope.technicalSpecifications}
          onChange={(e) => setFormData({
            ...formData,
            workScope: { ...formData.workScope, technicalSpecifications: e.target.value }
          })}
          placeholder="Detailed technical specifications and requirements..."
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Work Methodology"
          value={formData.workScope.workMethodology}
          onChange={(e) => setFormData({
            ...formData,
            workScope: { ...formData.workScope, workMethodology: e.target.value }
          })}
          placeholder="Describe the work methodology and approach..."
        />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Main Tasks</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setFormData({
                  ...formData,
                  workScope: {
                    ...formData.workScope,
                    mainTasks: [...formData.workScope.mainTasks, { task: '', description: '', estimatedHours: 0 }]
                  }
                });
              }}
            >
              Add Task
            </Button>
            
            {formData.workScope.mainTasks.map((task, index) => (
              <Grid container spacing={2} key={index} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Task Name"
                    value={task.task}
                    onChange={(e) => {
                      const updatedTasks = [...formData.workScope.mainTasks];
                      updatedTasks[index].task = e.target.value;
                      setFormData({
                        ...formData,
                        workScope: { ...formData.workScope, mainTasks: updatedTasks }
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={task.description}
                    onChange={(e) => {
                      const updatedTasks = [...formData.workScope.mainTasks];
                      updatedTasks[index].description = e.target.value;
                      setFormData({
                        ...formData,
                        workScope: { ...formData.workScope, mainTasks: updatedTasks }
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Hours"
                    type="number"
                    value={task.estimatedHours}
                    onChange={(e) => {
                      const updatedTasks = [...formData.workScope.mainTasks];
                      updatedTasks[index].estimatedHours = parseFloat(e.target.value) || 0;
                      setFormData({
                        ...formData,
                        workScope: { ...formData.workScope, mainTasks: updatedTasks }
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    color="error"
                    onClick={() => {
                      const updatedTasks = formData.workScope.mainTasks.filter((_, i) => i !== index);
                      setFormData({
                        ...formData,
                        workScope: { ...formData.workScope, mainTasks: updatedTasks }
                      });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Tab 8: Safety & Compliance
  const renderSafetyComplianceStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Safety & Compliance Requirements</Typography>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Safety Requirements</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.safetyCompliance.safetyRequirements.safetyOfficer}
                      onChange={(e) => setFormData({
                        ...formData,
                        safetyCompliance: {
                          ...formData.safetyCompliance,
                          safetyRequirements: {
                            ...formData.safetyCompliance.safetyRequirements,
                            safetyOfficer: e.target.checked
                          }
                        }
                      })}
                    />
                  }
                  label="Safety Officer Required"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.safetyCompliance.safetyRequirements.firstAid}
                      onChange={(e) => setFormData({
                        ...formData,
                        safetyCompliance: {
                          ...formData.safetyCompliance,
                          safetyRequirements: {
                            ...formData.safetyCompliance.safetyRequirements,
                            firstAid: e.target.checked
                          }
                        }
                      })}
                    />
                  }
                  label="First Aid Kit Required"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.safetyCompliance.safetyRequirements.fireExtinguisher}
                      onChange={(e) => setFormData({
                        ...formData,
                        safetyCompliance: {
                          ...formData.safetyCompliance,
                          safetyRequirements: {
                            ...formData.safetyCompliance.safetyRequirements,
                            fireExtinguisher: e.target.checked
                          }
                        }
                      })}
                    />
                  }
                  label="Fire Extinguisher Required"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Inspection Schedule</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.safetyCompliance.inspections.dailySafetyChecks}
                      onChange={(e) => setFormData({
                        ...formData,
                        safetyCompliance: {
                          ...formData.safetyCompliance,
                          inspections: {
                            ...formData.safetyCompliance.inspections,
                            dailySafetyChecks: e.target.checked
                          }
                        }
                      })}
                    />
                  }
                  label="Daily Safety Checks"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.safetyCompliance.inspections.weeklySafetyAudits}
                      onChange={(e) => setFormData({
                        ...formData,
                        safetyCompliance: {
                          ...formData.safetyCompliance,
                          inspections: {
                            ...formData.safetyCompliance.inspections,
                            weeklySafetyAudits: e.target.checked
                          }
                        }
                      })}
                    />
                  }
                  label="Weekly Safety Audits"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Emergency Procedures"
          value={formData.safetyCompliance.safetyRequirements.emergencyProcedures}
          onChange={(e) => setFormData({
            ...formData,
            safetyCompliance: {
              ...formData.safetyCompliance,
              safetyRequirements: {
                ...formData.safetyCompliance.safetyRequirements,
                emergencyProcedures: e.target.value
              }
            }
          })}
          placeholder="Describe emergency procedures and contact information..."
        />
      </Grid>
    </Grid>
  );

  // Tab 9: Documents
  const renderDocumentsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Document Management</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Upload all required documents. Supported formats: PDF, DOC, DOCX, JPG, PNG (Max size: 10MB each)
        </Alert>
      </Grid>

      {['workOrderDocument', 'technicalDrawings', 'contracts', 'permits', 'safetyDocuments'].map((docType) => (
        <Grid item xs={12} sm={6} key={docType}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {docType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </Typography>
              <input
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                id={`${docType}-upload`}
                type="file"
                multiple={docType !== 'workOrderDocument'}
                onChange={(e) => setUploadFiles({ 
                  ...uploadFiles, 
                  [docType]: docType === 'workOrderDocument' ? e.target.files[0] : Array.from(e.target.files)
                })}
              />
              <label htmlFor={`${docType}-upload`}>
                <Button variant="outlined" component="span" startIcon={<UploadIcon />} fullWidth>
                  Upload {docType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </Button>
              </label>
              {uploadFiles[docType] && (
                <Box sx={{ mt: 1 }}>
                  {Array.isArray(uploadFiles[docType]) ? (
                    uploadFiles[docType].map((file, index) => (
                      <Chip 
                        key={index}
                        label={file.name} 
                        color="success" 
                        variant="outlined" 
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))
                  ) : (
                    <Chip 
                      label={uploadFiles[docType].name} 
                      color="success" 
                      variant="outlined" 
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Tab 10: Status Tracking
  const renderStatusTrackingStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Status Tracking & Approvals</Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Current Status</InputLabel>
          <Select
            value={formData.statusTracking.currentStatus}
            onChange={(e) => setFormData({
              ...formData,
              statusTracking: { ...formData.statusTracking, currentStatus: e.target.value }
            })}
            label="Current Status"
          >
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="submitted">Submitted for Approval</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Typography variant="body1" gutterBottom>Overall Progress</Typography>
        <Slider
          value={formData.statusTracking.overallProgress}
          onChange={(e, newValue) => setFormData({
            ...formData,
            statusTracking: { ...formData.statusTracking, overallProgress: newValue }
          })}
          step={5}
          marks
          min={0}
          max={100}
          valueLabelDisplay="on"
          valueLabelFormat={(value) => `${value}%`}
        />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Approvals Required</Typography>
            <Grid container spacing={2}>
              {Object.keys(formData.statusTracking.approvals).map((approval) => (
                <Grid item xs={6} sm={3} key={approval}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.statusTracking.approvals[approval]}
                        onChange={(e) => setFormData({
                          ...formData,
                          statusTracking: {
                            ...formData.statusTracking,
                            approvals: {
                              ...formData.statusTracking.approvals,
                              [approval]: e.target.checked
                            }
                          }
                        })}
                      />
                    }
                    label={approval.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0: return renderBasicInfoStep();
      case 1: return renderContractorAssignmentStep();
      case 2: return renderWorkLocationStep();
      case 3: return renderTimelineStep();
      case 4: return renderWorkerRequirementsStep();
      case 5: return renderFinancialDetailsStep();
      case 6: return renderWorkScopeStep();
      case 7: return renderSafetyComplianceStep();
      case 8: return renderDocumentsStep();
      case 9: return renderStatusTrackingStep();
      default: return 'Unknown step';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%', mt: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ mt: 2, mb: 1 }}>
          {renderStepContent(activeStep)}
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep === steps.length - 1 ? (
            <Button 
              onClick={handleSave} 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : (workOrder ? 'Update Work Order' : 'Create Work Order')}
            </Button>
          ) : (
            <Button onClick={handleNext} variant="contained">
              Next
            </Button>
          )}
        </Box>
        
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Box>
    </LocalizationProvider>
  );
};

export default WorkOrderForm;